// src/store/api/favoriteApi.ts
import {baseApi} from "./baseApi";
import type {
  CreateFavoriteDto,
  FavoriteQueryDto,
  FavoriteResponseDto,
} from "../../types/favorite";

interface FavoriteStatsDto {
  totalFavorites: number;
  uniqueUsers: number;
  uniqueProperties: number;
  averageFavoritesPerUser: number;
  mostFavoritedProperty: {
    propertyId: number;
    title: string;
    favoriteCount: number;
  } | null;
  recentFavorites: number; // Favorites added in last 7 days
}

interface PaginatedFavoriteResponseDto {
  data: FavoriteResponseDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const favoriteApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get All Favorites (Admin)
    getAllFavorites: builder.query<
      PaginatedFavoriteResponseDto,
      FavoriteQueryDto
    >({
      query: (params) => ({
        url: "/favorites",
        params,
      }),
      providesTags: ["Favorite"],
    }),

    // Get User Favorites
    getUserFavorites: builder.query<
      PaginatedFavoriteResponseDto,
      {userId: number} & FavoriteQueryDto
    >({
      query: ({userId, ...params}) => ({
        url: `/favorites/user/${userId}`,
        params,
      }),
      providesTags: (result, error, {userId}) => [
        {type: "Favorite", id: `USER_${userId}`},
      ],
    }),

    // Add to Favorites
    addToFavorites: builder.mutation<
      FavoriteResponseDto,
      {userId: number} & CreateFavoriteDto
    >({
      query: ({userId, ...data}) => ({
        url: `/favorites/user/${userId}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, {userId, propertyId}) => [
        {type: "Favorite", id: `USER_${userId}`},
        {type: "Favorite", id: `PROPERTY_${propertyId}`},
        "Favorite",
      ],
    }),

    // Remove from Favorites by ID
    removeFavoriteById: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/favorites/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Favorite"],
    }),

    // Remove from Favorites by User and Property
    removeFavoriteByUserAndProperty: builder.mutation<
      {success: boolean; message: string},
      {userId: number; propertyId: number}
    >({
      query: ({userId, propertyId}) => ({
        url: `/favorites/user/${userId}/property/${propertyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, {userId, propertyId}) => [
        {type: "Favorite", id: `USER_${userId}`},
        {type: "Favorite", id: `PROPERTY_${propertyId}`},
        "Favorite",
      ],
    }),

    // Get Favorite Analytics/Stats
    getFavoriteStats: builder.query<FavoriteStatsDto, void>({
      query: () => "/favorites/analytics/stats",
      providesTags: ["Favorite"],
    }),

    // Check if Property is Favorited by User
    checkIsFavorited: builder.query<
      {isFavorited: boolean; favoriteId?: number},
      {userId: number; propertyId: number}
    >({
      query: ({userId, propertyId}) => ({
        url: `/favorites/user/${userId}/property/${propertyId}/check`,
      }),
      providesTags: (result, error, {userId, propertyId}) => [
        {type: "Favorite", id: `CHECK_${userId}_${propertyId}`},
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllFavoritesQuery,
  useGetUserFavoritesQuery,
  useAddToFavoritesMutation,
  useRemoveFavoriteByIdMutation,
  useRemoveFavoriteByUserAndPropertyMutation,
  useGetFavoriteStatsQuery,
  useCheckIsFavoritedQuery,
} = favoriteApi;
